import React, { useState, useEffect, useContext } from 'react';
import { Send, Package, Search, Calendar, User, ArrowRight, CheckCircle, AlertCircle, Plus, MapPin, Route, Map, Truck } from 'lucide-react';
import axios from 'axios';
import jsPDF from 'jspdf';
import { UserContext } from '../../contexts/UserContext';
import WarehouseMap from '../warehouse/WarehouseMap';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'https://ventro.energy';

const WarehouseIssue = () => {
  const { user } = useContext(UserContext);
  const [projects, setProjects] = useState([]);
  const [items, setItems] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [issueItems, setIssueItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [issuedBy, setIssuedBy] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [availableCategories, setAvailableCategories] = useState([]);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
  const [shipments, setShipments] = useState([]);
  const [selectedShipment, setSelectedShipment] = useState('');
  const [showShipmentModal, setShowShipmentModal] = useState(false);
  const [shipmentForm, setShipmentForm] = useState({
    name: '',
    price: ''
  });
  const [showRouteModal, setShowRouteModal] = useState(false);
  const [optimizedRoute, setOptimizedRoute] = useState([]);
  const [showMap, setShowMap] = useState(false);
  // Vehicle and Person Assignment fields
  const [vehicles, setVehicles] = useState([]);
  const [people, setPeople] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [selectedPerson, setSelectedPerson] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Set default issuedBy when user data is available
    if (user && user.first_name && user.last_name) {
      setIssuedBy(`${user.first_name} ${user.last_name}`);
    } else if (user && user.first_name) {
      setIssuedBy(user.first_name);
    } else if (user && user.username) {
      setIssuedBy(user.username);
    }
  }, [user]);

  const fetchData = async () => {
    try {
      setLoading(true);
      await fetchProjects();
      await fetchItems();
      await fetchShipments();
      await fetchVehicles();
      await fetchPeople();
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const projectsResponse = await axios.get(`${API_BASE_URL}/api/projects/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      const projectsData = Array.isArray(projectsResponse.data) ? projectsResponse.data : 
                          projectsResponse.data.results ? projectsResponse.data.results : [];
      setProjects(projectsData.map(project => ({
        id: project.id,
        name: project.name,
        projectNumber: project.project_number || `PRJ-${project.id}`,
        manager: project.project_manager || 'Not assigned'
      })));
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchItems = async () => {
    try {
      const itemsResponse = await axios.get(`${API_BASE_URL}/api/warehouse/items/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      const itemsDataArray = Array.isArray(itemsResponse.data) ? itemsResponse.data : 
                            itemsResponse.data.results ? itemsResponse.data.results : [];
      const itemsData = itemsDataArray.map(item => ({
        id: item.id,
        name: item.name,
        category: item.category_name,
        availableQty: item.quantity_in_stock,
        unit: item.unit,
        location: item.location,
        unitPrice: parseFloat(item.unit_price)
      }));
      
      setItems(itemsData);
      
      // Extract unique categories and locations
      const categories = [...new Set(itemsData.map(item => item.category))].filter(Boolean).sort();
      const locations = [...new Set(itemsData.map(item => item.location))].filter(Boolean).sort();
      
      setAvailableCategories(categories);
      setAvailableLocations(locations);
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  };

  const fetchShipments = async () => {
    try {
      const shipmentsResponse = await axios.get(`${API_BASE_URL}/api/warehouse/shipments/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      const shipmentsData = Array.isArray(shipmentsResponse.data) ? shipmentsResponse.data : 
                           shipmentsResponse.data.results ? shipmentsResponse.data.results : [];
      setShipments(shipmentsData.map(shipment => ({
        id: shipment.id,
        name: shipment.name,
        price: parseFloat(shipment.price || 0),
        project: shipment.project,
        projectName: shipment.project_name
      })));
    } catch (error) {
      console.error('Error fetching shipments:', error);
    }
  };

  const fetchVehicles = async () => {
    try {
      // Use the new endpoint that only returns available vehicles
      const vehiclesResponse = await axios.get(`${API_BASE_URL}/api/vehicles/available/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      const vehiclesData = vehiclesResponse.data || [];
      setVehicles(Array.isArray(vehiclesData) ? vehiclesData : []);
    } catch (error) {
      console.error('Error fetching available vehicles:', error);
      // Fallback to old endpoint if new one fails
      try {
        const fallbackResponse = await axios.get(`${API_BASE_URL}/api/cars/`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });
        const fallbackData = fallbackResponse.data.results || fallbackResponse.data || [];
        // Filter only truly available vehicles - use strict whitelist approach
        const availableVehicles = fallbackData.filter(vehicle => {
          const status = vehicle.car_status;
          const forbiddenStatuses = [
            'loading', 'issued', 'assigned_loading', 'in_transit', 'in_transit_to_project', 
            'at_project_site', 'delivered', 'service', 'reservation', 'damaged'
          ];
          const isAvailable = status === 'available' && !forbiddenStatuses.includes(status);
          console.log(`Vehicle ${vehicle.plate_number}: status = ${status}, available = ${isAvailable}`);
          return isAvailable;
        });
        setVehicles(Array.isArray(availableVehicles) ? availableVehicles : []);
      } catch (fallbackError) {
        console.error('Error fetching vehicles (fallback):', fallbackError);
        setVehicles([]);
      }
    }
  };

  const fetchPeople = async () => {
    try {
      const peopleResponse = await axios.get(`${API_BASE_URL}/api/users/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      const peopleData = peopleResponse.data.results || peopleResponse.data || [];
      setPeople(Array.isArray(peopleData) ? peopleData : []);
    } catch (error) {
      console.error('Error fetching people:', error);
      setPeople([]);
    }
  };

  const handleShipmentSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = {
        name: shipmentForm.name,
        price: parseFloat(shipmentForm.price)
      };
      
      await axios.post(`${API_BASE_URL}/api/warehouse/shipments/`, formData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        }
      });
      setShowShipmentModal(false);
      setShipmentForm({ name: '', price: '' });
      fetchShipments(); // Refresh shipments list
      alert('Shipment created successfully!');
    } catch (error) {
      console.error('Error creating shipment:', error);
      alert('Error creating shipment. Please try again.');
    }
  };


  const handleCreateProject = async (projectData) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/projects/`, projectData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        }
      });
      await fetchProjects(); // Refresh projects list
      setSelectedProject(response.data.id); // Auto-select the new project
      setShowCreateProjectModal(false);
      alert('Project created successfully!');
    } catch (error) {
      console.error('Error creating project:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          JSON.stringify(error.response?.data) ||
                          'Error creating project. Please try again.';
      alert(`Error creating project: ${errorMessage}`);
    }
  };


  const generatePDF = async (issueData) => {
    try {
      // Create PDF content
      const doc = new jsPDF();
      
      // Header
      doc.setFontSize(20);
      doc.text('WAREHOUSE ISSUE REPORT', 20, 20);
      
      doc.setFontSize(12);
      doc.text(`Issue Number: ${issueData.issueNumber}`, 20, 35);
      
      // Add recipient info
      if (issueData.projectName) {
        doc.text(`Project: ${issueData.projectName}`, 20, 45);
      } else if (issueData.assigned_to_person) {
        doc.text(`Issued to Person: ${issueData.assigned_to_person}`, 20, 45);
      }
      
      // Status
      doc.setTextColor(0, 100, 0);
      doc.text(`Status: ISSUED`, 120, 35);
      doc.setTextColor(0, 0, 0);
      
      doc.text(`Date: ${new Date(issueData.issuedAt).toLocaleDateString()}`, 20, 55);
      doc.text(`Total Value: €${issueData.totalValue.toFixed(2)}`, 20, 65);
      
      // Table header
      doc.text('ITEMS ISSUED:', 20, 85);
      doc.text('Item Name', 20, 95);
      doc.text('Quantity', 100, 95);
      doc.text('Unit Price', 140, 95);
      doc.text('Total', 180, 95);
      
      // Draw line under header
      doc.line(20, 97, 200, 97);
      
      // Table content
      let yPos = 105;
      issueData.items.forEach(item => {
        doc.text(item.item_name || 'Unknown Item', 20, yPos);
        doc.text(item.quantity_issued?.toString() || '0', 100, yPos);
        doc.text(`€${(parseFloat(item.unit_price_at_issue) || 0).toFixed(2)}`, 140, yPos);
        doc.text(`€${((item.quantity_issued || 0) * (parseFloat(item.unit_price_at_issue) || 0)).toFixed(2)}`, 180, yPos);
        yPos += 10;
      });
      
      // Footer
      doc.text(`Generated on: ${new Date().toLocaleString()}`, 20, yPos + 20);
      
      // Save PDF
      doc.save(`warehouse-issue-${issueData.issueNumber}.pdf`);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  const generatePreviewPDF = async () => {
    try {
      if (!selectedProject || issueItems.length === 0) return;

      const selectedProjectData = projects.find(p => p.id === parseInt(selectedProject));
      const totalValue = issueItems.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);

      const previewData = {
        issueNumber: `PREVIEW-${Date.now()}`,
        projectName: selectedProjectData?.name || 'Unknown Project',
        issuedAt: new Date().toISOString(),
        totalValue: totalValue,
        items: issueItems.map(item => ({
          item_name: item.name,
          quantity_issued: item.quantity,
          unit_price_at_issue: item.unitPrice,
          quantity_returned: 0
        }))
      };

      // Create PDF content
      const doc = new jsPDF();
      
      // Header
      doc.setFontSize(20);
      doc.text('WAREHOUSE ISSUE REPORT (PREVIEW)', 20, 20);
      
      doc.setFontSize(12);
      doc.text(`Project: ${previewData.projectName}`, 20, 35);
      doc.text(`Date: ${new Date().toLocaleDateString()}`, 20, 45);
      doc.text(`Issued By: ${issuedBy || 'Not specified'}`, 20, 55);
      doc.text(`Notes: ${notes || 'No notes'}`, 20, 65);
      
      // Table header
      doc.text('ITEMS TO BE ISSUED:', 20, 85);
      doc.text('Item Name', 20, 95);
      doc.text('Quantity', 120, 95);
      doc.text('Unit', 160, 95);
      
      // Draw line under header
      doc.line(20, 97, 180, 97);
      
      // Table content
      let yPos = 105;
      previewData.items.forEach(item => {
        // Find the item in the items array to get the unit
        const itemData = items.find(i => i.name === item.item_name);
        const unit = itemData?.unit || 'pcs';
        
        doc.text(item.item_name, 20, yPos);
        doc.text(item.quantity_issued.toString(), 120, yPos);
        doc.text(unit, 160, yPos);
        yPos += 10;
      });
      
      // Footer
      doc.text(`Generated on: ${new Date().toLocaleString()}`, 20, yPos + 20);
      doc.text('NOTE: This is a preview. Issue must be confirmed to take effect.', 20, yPos + 35);
      
      // Save PDF
      doc.save(`warehouse-issue-preview-${new Date().toISOString().split('T')[0]}.pdf`);
      
    } catch (error) {
      console.error('Error generating preview PDF:', error);
      alert('Error generating preview PDF. Please try again.');
    }
  };

  const filteredItems = items.filter(item => {
    // Search filter
    const matchesSearch = searchTerm === '' || 
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.location.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Category filter
    const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
    
    // Location filter
    const matchesLocation = locationFilter === 'all' || item.location === locationFilter;
    
    return matchesSearch && matchesCategory && matchesLocation;
  });

  const addItemToIssue = (item) => {
    const existingItem = issueItems.find(issueItem => issueItem.id === item.id);
    if (existingItem) {
      setIssueItems(issueItems.map(issueItem =>
        issueItem.id === item.id
          ? { ...issueItem, quantity: Math.min(issueItem.quantity + 1, item.availableQty) }
          : issueItem
      ));
    } else {
      setIssueItems([...issueItems, { ...item, quantity: 1 }]);
    }
  };

  const updateIssueQuantity = (itemId, quantity) => {
    const item = items.find(i => i.id === itemId);
    const validQuantity = Math.max(0, Math.min(quantity, item.availableQty));
    
    if (validQuantity === 0) {
      setIssueItems(issueItems.filter(issueItem => issueItem.id !== itemId));
    } else {
      setIssueItems(issueItems.map(issueItem =>
        issueItem.id === itemId
          ? { ...issueItem, quantity: validQuantity }
          : issueItem
      ));
    }
  };

  const removeItemFromIssue = (itemId) => {
    setIssueItems(issueItems.filter(issueItem => issueItem.id !== itemId));
  };

  const generateOptimalRoute = () => {
    if (issueItems.length === 0) {
      alert('Add items to issue first to generate route');
      return;
    }

    // Extract unique locations from issue items
    const locations = issueItems.map(item => {
      const foundItem = items.find(i => i.id === item.id);
      return {
        location: foundItem?.location || 'Unknown',
        items: [{ name: foundItem?.name, quantity: item.quantity, unit: foundItem?.unit }]
      };
    });

    // Group items by location
    const locationGroups = {};
    locations.forEach(loc => {
      if (!locationGroups[loc.location]) {
        locationGroups[loc.location] = [];
      }
      locationGroups[loc.location].push(...loc.items);
    });

    // Convert to array and sort locations optimally
    const sortedLocations = Object.entries(locationGroups).map(([location, items]) => ({
      location,
      items,
      sortKey: location // Simple alphabetical sort for now
    })).sort((a, b) => {
      // Advanced sorting logic: Rack -> Shelf -> Container
      const parseLocation = (loc) => {
        const parts = loc.split('-');
        if (parts.length === 3) {
          return {
            rack: parts[0],
            shelf: parseInt(parts[1]) || 0,
            container: parts[2]
          };
        }
        return { rack: loc, shelf: 0, container: 'A' };
      };

      const locA = parseLocation(a.location);
      const locB = parseLocation(b.location);

      // Sort by rack first
      if (locA.rack !== locB.rack) {
        return (locA.rack || '').localeCompare(locB.rack || '');
      }
      // Then by shelf
      if (locA.shelf !== locB.shelf) {
        return locA.shelf - locB.shelf;
      }
      // Finally by container
      return (locA.container || '').localeCompare(locB.container || '');
    });

    setOptimizedRoute(sortedLocations);
    setShowRouteModal(true);
  };

  const handleSubmitIssue = async (e) => {
    e.preventDefault();
    if (!selectedProject || issueItems.length === 0) return;
    
    // Validate driver is selected when vehicle is selected
    if (selectedVehicle && !selectedPerson) {
      alert('Please select a driver when assigning a vehicle.');
      return;
    }

    // Validate vehicle is still available before submitting
    if (selectedVehicle) {
      try {
        // Check vehicle status
        const vehicleCheckResponse = await axios.get(`${API_BASE_URL}/api/cars/${selectedVehicle}/`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });
        const currentVehicleStatus = vehicleCheckResponse.data.car_status;
        
        if (currentVehicleStatus !== 'available') {
          alert(`⚠️ Vehicle is no longer available! Current status: ${currentVehicleStatus}. Please select a different vehicle.`);
          // Refresh vehicle list
          await fetchVehicles();
          return;
        }

        // Additional check: verify the vehicle is in the available vehicles list
        // This catches cases where the vehicle might have active assignments
        try {
          const availableVehiclesResponse = await axios.get(`${API_BASE_URL}/api/vehicles/available/`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`
            }
          });
          const availableVehicleIds = (availableVehiclesResponse.data || []).map(v => v.id);
          
          if (!availableVehicleIds.includes(parseInt(selectedVehicle))) {
            alert(`⚠️ Vehicle is no longer available for assignment (may have active items from another project). Please select a different vehicle.`);
            // Refresh vehicle list
            await fetchVehicles();
            return;
          }
        } catch (availabilityError) {
          console.warn('Could not verify vehicle availability from dedicated endpoint, proceeding with status check only');
        }
        
      } catch (error) {
        console.error('Error checking vehicle status:', error);
        alert('Error checking vehicle availability. Please try again.');
        return;
      }
    }

    setSubmitting(true);
    
    try {
      const issueData = {
        project: parseInt(selectedProject),
        notes: notes,
        expected_return_date: null, // You can add a date picker for this
        items_data: issueItems.map(item => ({
          item_id: item.id,
          quantity: item.quantity,
          notes: ''
        }))
      };

      const issueResponse = await axios.post(`${API_BASE_URL}/api/warehouse/warehouse-issues/`, issueData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        }
      });

      // Create vehicle and person assignments if selected
      const createdIssue = issueResponse.data;
      const assignmentPromises = [];
      let vehicleAssigned = false; // Track if vehicle was already assigned
      
      // Get project data once for all assignments
      const selectedProjectData = projects.find(p => p.id === parseInt(selectedProject));

      // Process each issued item for assignments
      for (let i = 0; i < issueItems.length; i++) {
        const issueItem = issueItems[i];
        
        // Create vehicle assignment if vehicle is selected (assign vehicle to project/person)
        if (selectedVehicle && !vehicleAssigned) {
          const assignedUser = selectedPerson || user.id; // Use selected person or current user
          
          const vehicleAssignmentData = {
            car: parseInt(selectedVehicle),
            user: parseInt(assignedUser),
            project: parseInt(selectedProject), // Add project ID
            assignment_reason: `Project: ${selectedProjectData?.name || 'Unknown Project'}`,
            assignment_notes: `Vehicle assigned for project work. Issue #${createdIssue.issue_number || (createdIssue.id ? 'ISS-' + String(createdIssue.id).padStart(6, '0') : 'undefined')}`,
            status: 'assigned',
            mileage_start: null // Will be set when vehicle is actually taken
          };
          
          assignmentPromises.push(
            axios.post(`${API_BASE_URL}/api/vehicle-assignments/`, vehicleAssignmentData, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                'Content-Type': 'application/json'
              }
            }).then(() => {
              // Update vehicle status to 'assigned_loading' using new status system
              return axios.post(`${API_BASE_URL}/api/vehicles/${selectedVehicle}/change-status/`, {
                status: 'assigned_loading',
                location: 'Warehouse - Loading Bay',
                notes: `Loading items for delivery to ${selectedProjectData?.name || 'project'}`
              }, {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                  'Content-Type': 'application/json'
                }
              });
            })
          );
          vehicleAssigned = true; // Prevent multiple assignments of same vehicle
        }

        // Create VehicleItemAssignment for each item if vehicle is selected
        if (selectedVehicle && issueItem.quantity > 0) {
          // We need to find the created ProjectIssueItem ID
          // Since we don't have it immediately, we'll create it in a follow-up request
          assignmentPromises.push(
            // First, get the created issue items
            axios.get(`${API_BASE_URL}/api/warehouse/warehouse-issues/${createdIssue.id}/`, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`
              }
            }).then(issueDetailResponse => {
              const issueItems = issueDetailResponse.data.items || [];
              const matchingItem = issueItems.find(item => item.item === issueItem.item);
              
              if (matchingItem) {
                // Create VehicleItemAssignment
                return axios.post(`${API_BASE_URL}/api/warehouse/vehicle-assignments/`, {
                  project_issue_item: matchingItem.id,
                  vehicle: parseInt(selectedVehicle),
                  quantity: issueItem.quantity,
                  status: 'assigned',
                  notes: `Assigned for delivery to ${selectedProjectData?.name || 'project'}`
                }, {
                  headers: {
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                    'Content-Type': 'application/json'
                  }
                });
              }
            })
          );
        }

        // Note: We don't create PersonItemAssignments for project issues
        // because items are already allocated to the project.
        // PersonItemAssignments are for direct item-to-person assignments
        // outside of project context.
      }

      // Wait for all assignments to complete
      if (assignmentPromises.length > 0) {
        await Promise.all(assignmentPromises);
      }
      
      let successMessage = 'Items successfully issued to project!';
      if (selectedVehicle) {
        const vehicle = vehicles.find(v => v.id === parseInt(selectedVehicle));
        const assignedUser = selectedPerson ? people.find(p => p.id === parseInt(selectedPerson)) : null;
        successMessage += ` Vehicle ${vehicle?.car_brand} ${vehicle?.car_model} (${vehicle?.plate_number}) assigned`;
        if (assignedUser) {
          successMessage += ` to ${assignedUser?.first_name} ${assignedUser?.last_name}`;
        }
        successMessage += ' for this project.';
      }
      if (selectedPerson && !selectedVehicle) {
        const person = people.find(p => p.id === parseInt(selectedPerson));
        successMessage += ` ${person?.first_name} ${person?.last_name} is responsible for these items.`;
      }
      
      alert(successMessage);
      
      // Reset form (keep issuedBy with user name)
      setSelectedProject('');
      setIssueItems([]);
      setNotes('');
      setSelectedShipment('');
      setSelectedVehicle('');
      setSelectedPerson('');
      
      // Reset issuedBy to current user
      if (user && user.first_name && user.last_name) {
        setIssuedBy(`${user.first_name} ${user.last_name}`);
      } else if (user && user.first_name) {
        setIssuedBy(user.first_name);
      } else if (user && user.username) {
        setIssuedBy(user.username);
      }
      
      // Refresh available items
      fetchData();
      
    } catch (error) {
      console.error('Error creating issue:', error);
      console.error('Error response data:', error.response?.data);
      
      if (error.response?.status === 403) {
        alert('Permission denied. Please check your access rights or contact an administrator.');
      } else if (error.response?.status === 400) {
        const errorMessage = error.response?.data?.detail || 
                           JSON.stringify(error.response?.data) || 
                           'Invalid request data';
        alert(`Error: ${errorMessage}`);
      } else {
        alert('Error issuing items. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-300 rounded w-1/4 mb-4"></div>
            <div className="h-4 bg-gray-300 rounded w-1/2 mb-8"></div>
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-32 bg-gray-300 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <Send className="mr-3 h-8 w-8 text-blue-600" />
                Issue Items to Project
              </h1>
              <p className="text-gray-600 mt-2">Issue inventory items to specific projects and track their usage</p>
            </div>
            <button
              onClick={() => setShowMap(!showMap)}
              className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                showMap 
                  ? 'bg-blue-600 text-white hover:bg-blue-700' 
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              <Map className="h-4 w-4 mr-2" />
              {showMap ? 'Hide Map' : 'Show Map'}
            </button>
          </div>
        </div>

        {/* Warehouse Map */}
        {showMap && (
          <div className="mb-8">
            <WarehouseMap 
              showOccupancy={true}
            />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Project Selection */}
          <div className="space-y-6">
            {/* Project Selection */}
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Select Project</h3>
                <button
                  onClick={() => setShowCreateProjectModal(true)}
                  className="flex items-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                  title="Create new project"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  New Project
                </button>
              </div>
              <select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="">Choose a project...</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.name} ({project.projectNumber})
                  </option>
                ))}
              </select>
              {selectedProject && (
                <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">Project Details</h4>
                  <div className="space-y-1 text-sm text-blue-800">
                    <p className="flex items-center">
                      <User className="h-4 w-4 inline mr-2" />
                      <span className="font-medium">Manager:</span>
                      <span className="ml-1">{projects.find(p => p.id === parseInt(selectedProject))?.manager}</span>
                    </p>
                    <p className="flex items-center">
                      <Package className="h-4 w-4 inline mr-2" />
                      <span className="font-medium">Project ID:</span>
                      <span className="ml-1">{projects.find(p => p.id === parseInt(selectedProject))?.projectNumber}</span>
                    </p>
                    <p className="flex items-center">
                      <MapPin className="h-4 w-4 inline mr-2" />
                      <span className="font-medium">Location:</span>
                      <span className="ml-1">{projects.find(p => p.id === parseInt(selectedProject))?.location || 'Not specified'}</span>
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Issue Form */}
            <form onSubmit={handleSubmitIssue} className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <ArrowRight className="h-5 w-5 mr-2" />
                Issue Summary
              </h3>

              {/* Issued By */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Issued By</label>
                <input
                  type="text"
                  value={issuedBy}
                  onChange={(e) => setIssuedBy(e.target.value)}
                  placeholder="Your name"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Items to Issue */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Items to Issue</label>
                {issueItems.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Package className="h-8 w-8 mx-auto mb-2" />
                    <p>No items selected</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {issueItems.map(item => (
                      <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{item.name}</div>
                          <div className="text-sm text-gray-500">Available: {item.availableQty} {item.unit}</div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <input
                            type="number"
                            min="1"
                            max={item.availableQty}
                            value={item.quantity}
                            onChange={(e) => updateIssueQuantity(item.id, parseInt(e.target.value) || 0)}
                            className="w-16 p-1 border border-gray-300 rounded text-center"
                          />
                          <span className="text-sm text-gray-500">{item.unit}</span>
                          <button
                            type="button"
                            onClick={() => removeItemFromIssue(item.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Shipment Selection */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Select Shipment (Optional)</label>
                  <button
                    type="button"
                    onClick={() => setShowShipmentModal(true)}
                    className="flex items-center px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors text-sm"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Create Shipment
                  </button>
                </div>
                <select
                  value={selectedShipment}
                  onChange={(e) => setSelectedShipment(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">No shipment selected</option>
                  {shipments
                    .filter(shipment => !selectedProject || !shipment.project || shipment.project === parseInt(selectedProject))
                    .map(shipment => (
                      <option key={shipment.id} value={shipment.id}>
                        {shipment.name} - €{shipment.price.toFixed(2)}
                        {shipment.projectName && ` (${shipment.projectName})`}
                      </option>
                    ))}
                </select>
                {selectedShipment && (
                  <div className="mt-2 p-3 bg-indigo-50 rounded-lg">
                    <p className="text-sm text-indigo-800">
                      Shipment: {shipments.find(s => s.id === parseInt(selectedShipment))?.name} - 
                      €{shipments.find(s => s.id === parseInt(selectedShipment))?.price.toFixed(2)}
                    </p>
                  </div>
                )}
              </div>

              {/* Vehicle Assignment */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <Truck className="h-4 w-4 mr-2" />
                  Assign to Vehicle (Optional)
                </label>
                <select
                  value={selectedVehicle}
                  onChange={(e) => setSelectedVehicle(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">No vehicle selected</option>
                  {Array.isArray(vehicles) ? vehicles.map(vehicle => (
                    <option key={vehicle.id} value={vehicle.id}>
                      {vehicle.car_brand} {vehicle.car_model} ({vehicle.plate_number})
                    </option>
                  )) : []}
                </select>
                {selectedVehicle && (
                  <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <Truck className="h-4 w-4 inline mr-1" />
                      Vehicle: {vehicles.find(v => v.id === parseInt(selectedVehicle))?.car_brand} {vehicles.find(v => v.id === parseInt(selectedVehicle))?.car_model} 
                      ({vehicles.find(v => v.id === parseInt(selectedVehicle))?.plate_number})
                    </p>
                  </div>
                )}
              </div>

              {/* Driver Assignment */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <User className="h-4 w-4 mr-2" />
                  Assign Driver/Responsible Person {selectedVehicle ? '(Required for Vehicle)' : '(Optional)'}
                </label>
                <select
                  value={selectedPerson}
                  onChange={(e) => setSelectedPerson(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">{selectedVehicle ? 'Select driver for this vehicle' : 'No person selected'}</option>
                  {Array.isArray(people) ? people.map(person => (
                    <option key={person.id} value={person.id}>
                      {person.first_name} {person.last_name} ({person.email})
                    </option>
                  )) : []}
                </select>
                {selectedPerson && (
                  <div className="mt-2 p-3 bg-green-50 rounded-lg">
                    <p className="text-sm text-green-800">
                      <User className="h-4 w-4 inline mr-1" />
                      {selectedVehicle ? 'Driver' : 'Responsible Person'}: {people.find(p => p.id === parseInt(selectedPerson))?.first_name} {people.find(p => p.id === parseInt(selectedPerson))?.last_name}
                      ({people.find(p => p.id === parseInt(selectedPerson))?.email})
                    </p>
                  </div>
                )}
              </div>

              {/* Notes */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes (Optional)</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Additional notes about this issue..."
                  rows="3"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Assignment Summary */}
              {(selectedProject && (selectedVehicle || selectedPerson)) && (
                <div className="mb-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <h4 className="text-sm font-medium text-yellow-900 mb-3 flex items-center">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Assignment Summary
                  </h4>
                  <div className="space-y-2 text-sm text-yellow-800">
                    <p><span className="font-medium">Project:</span> {projects.find(p => p.id === parseInt(selectedProject))?.name}</p>
                    <p><span className="font-medium">Project Manager:</span> {projects.find(p => p.id === parseInt(selectedProject))?.manager}</p>
                    {selectedVehicle && (
                      <p><span className="font-medium">Vehicle:</span> {vehicles.find(v => v.id === parseInt(selectedVehicle))?.car_brand} {vehicles.find(v => v.id === parseInt(selectedVehicle))?.car_model} ({vehicles.find(v => v.id === parseInt(selectedVehicle))?.plate_number})</p>
                    )}
                    {selectedPerson && (
                      <p><span className="font-medium">{selectedVehicle ? 'Driver' : 'Responsible Person'}:</span> {people.find(p => p.id === parseInt(selectedPerson))?.first_name} {people.find(p => p.id === parseInt(selectedPerson))?.last_name}</p>
                    )}
                    <p><span className="font-medium">Total Items:</span> {issueItems.reduce((sum, item) => sum + item.quantity, 0)} items ({issueItems.length} different types)</p>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="space-y-3">
                {/* Route Generation Button */}
                <button
                  type="button"
                  onClick={generateOptimalRoute}
                  disabled={issueItems.length === 0}
                  className="w-full flex items-center justify-center px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
                >
                  <Route className="h-4 w-4 mr-2" />
                  Generate Collection Route
                </button>
                
                <div className="flex space-x-3">
                  {/* Generate PDF Button */}
                  <button
                    type="button"
                    onClick={() => generatePreviewPDF()}
                    disabled={!selectedProject || issueItems.length === 0}
                    className="flex-1 flex items-center justify-center px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
                  >
                    <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Preview PDF
                  </button>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={!selectedProject || issueItems.length === 0 || submitting}
                    className="flex-1 flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
                  >
                    {submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Processing...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Issue Items
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>

            {/* Info Card */}
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-2" />
                <div>
                  <h4 className="text-sm font-medium text-blue-800 mb-1">Important Notes</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Items will be deducted from inventory immediately</li>
                    <li>• Project managers will be notified of issued items</li>
                    <li>• Return process should be initiated when items are no longer needed</li>
                  </ul>
                </div>
              </div>
            </div>

          </div>

          {/* Right Column - Item Search and Table */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Available Items</h3>
              {/* Search and Filters */}
              <div className="space-y-4 mb-4">
                {/* Search */}
                <div className="relative">
                  <Search className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search items by name, category, or location..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                {/* Filters */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <select
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="all">All Categories</option>
                      {availableCategories.map(category => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                    <select
                      value={locationFilter}
                      onChange={(e) => setLocationFilter(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="all">All Locations</option>
                      {availableLocations.map(location => (
                        <option key={location} value={location}>
                          {location}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="flex items-end">
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setCategoryFilter('all');
                        setLocationFilter('all');
                      }}
                      className="w-full px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto max-h-[600px] overflow-y-auto border border-gray-200 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Available</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Price</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredItems.map(item => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{item.name}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-600">{item.category}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            <span className={item.availableQty > 0 ? 'text-green-600 font-medium' : 'text-red-600'}>
                              {item.availableQty}
                            </span>
                            <span className="text-gray-500 ml-1">{item.unit}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-600">{item.location}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-900">€{item.unitPrice.toFixed(2)}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <button
                            onClick={() => addItemToIssue(item)}
                            disabled={item.availableQty === 0}
                            className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium"
                          >
                            {item.availableQty === 0 ? 'Out of Stock' : 'Add'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredItems.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <Package className="h-8 w-8 mx-auto mb-2" />
                    <p>No items found matching your search</p>
                  </div>
                )}
              </div>
              
              <div className="mt-4 text-sm text-gray-500 text-center">
                Showing {filteredItems.length} of {items.length} items
                {(searchTerm || categoryFilter !== 'all' || locationFilter !== 'all') && (
                  <span className="ml-2 text-blue-600">
                    (filtered)
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Create Project Modal */}
        {showCreateProjectModal && (
          <CreateProjectModal
            onSave={handleCreateProject}
            onClose={() => setShowCreateProjectModal(false)}
          />
        )}

        {/* Shipment Modal */}
        {showShipmentModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Shipment</h3>
                <form onSubmit={handleShipmentSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Shipment Name
                    </label>
                    <input
                      type="text"
                      value={shipmentForm.name}
                      onChange={(e) => setShipmentForm({...shipmentForm, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Price (€)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={shipmentForm.price}
                      onChange={(e) => setShipmentForm({...shipmentForm, price: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div className="flex space-x-3 pt-4">
                    <button
                      type="submit"
                      className="flex-1 bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      Create Shipment
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowShipmentModal(false)}
                      className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Route Modal */}
        {showRouteModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 shadow-lg rounded-md bg-white max-h-[90vh] overflow-hidden">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                    <Route className="h-6 w-6 mr-2 text-purple-600" />
                    Optimal Collection Route
                  </h3>
                  <button 
                    onClick={() => setShowRouteModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>
                </div>

                <div className="mb-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <p className="text-sm text-purple-800">
                    <strong>Route optimized for minimal walking distance!</strong> 
                    Follow this sequence to efficiently collect all items from the warehouse.
                  </p>
                </div>

                <div className="max-h-[60vh] overflow-y-auto">
                  <div className="space-y-4">
                    {optimizedRoute.map((stop, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center mb-3">
                          <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">
                            {index + 1}
                          </div>
                          <div>
                            <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                              <MapPin className="h-4 w-4 mr-1 text-purple-600" />
                              Location: {stop.location}
                            </h4>
                            <p className="text-sm text-gray-600">
                              {stop.items.length} item{stop.items.length > 1 ? 's' : ''} to collect
                            </p>
                          </div>
                        </div>
                        
                        <div className="ml-11">
                          <div className="space-y-2">
                            {stop.items.map((item, itemIndex) => (
                              <div key={itemIndex} className="flex justify-between items-center bg-white p-2 rounded border">
                                <span className="font-medium text-gray-900">{item.name}</span>
                                <span className="text-sm text-gray-600">
                                  Qty: {item.quantity} {item.unit}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-600">
                      Total stops: <strong>{optimizedRoute.length}</strong> | 
                      Total items: <strong>{optimizedRoute.reduce((sum, stop) => sum + stop.items.length, 0)}</strong>
                    </div>
                    <button
                      onClick={() => setShowRouteModal(false)}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      Got it!
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

// Create Project Modal Component
const CreateProjectModal = ({ onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    project_number: '',
    project_manager: '',
    customer: '',
    country: '',
    start_date: '',
    end_date: '',
    type_of_turbine: '',
    number_of_turbines: '',
    max_days_per_employers: '',
    color: '#3B82F6',
    comment: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name.trim()) {
      alert('Please enter a project name');
      return;
    }

    // Clean up the data before sending
    const projectData = {
      ...formData,
      name: formData.name.trim(),
      project_number: formData.project_number.trim() || undefined,
      project_manager: formData.project_manager.trim() || undefined,
      customer: formData.customer.trim() || undefined,
      country: formData.country.trim() || undefined,
      start_date: formData.start_date || undefined,
      end_date: formData.end_date || undefined,
      type_of_turbine: formData.type_of_turbine.trim() || undefined,
      number_of_turbines: formData.number_of_turbines.trim() || undefined,
      max_days_per_employers: formData.max_days_per_employers.trim() || undefined,
      comment: formData.comment.trim() || undefined
    };

    // Remove undefined values
    Object.keys(projectData).forEach(key => {
      if (projectData[key] === undefined) {
        delete projectData[key];
      }
    });

    onSave(projectData);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Create New Project</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Basic Information */}
            <div className="border-b pb-4">
              <h4 className="text-md font-medium text-gray-900 mb-3">Basic Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Project Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter project name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Project Number
                  </label>
                  <input
                    type="text"
                    value={formData.project_number}
                    onChange={(e) => handleChange('project_number', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., PRJ-2024-001"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Project Manager
                  </label>
                  <input
                    type="text"
                    value={formData.project_manager}
                    onChange={(e) => handleChange('project_manager', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter project manager name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Customer
                  </label>
                  <input
                    type="text"
                    value={formData.customer}
                    onChange={(e) => handleChange('customer', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter customer name"
                  />
                </div>
              </div>
            </div>

            {/* Project Details */}
            <div className="border-b pb-4">
              <h4 className="text-md font-medium text-gray-900 mb-3">Project Details</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Country
                  </label>
                  <input
                    type="text"
                    value={formData.country}
                    onChange={(e) => handleChange('country', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter country"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type of Turbine
                  </label>
                  <input
                    type="text"
                    value={formData.type_of_turbine}
                    onChange={(e) => handleChange('type_of_turbine', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Vestas V90-3.0MW"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Number of Turbines
                  </label>
                  <input
                    type="text"
                    value={formData.number_of_turbines}
                    onChange={(e) => handleChange('number_of_turbines', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., 25"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Days per Employee
                  </label>
                  <input
                    type="text"
                    value={formData.max_days_per_employers}
                    onChange={(e) => handleChange('max_days_per_employers', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., 14"
                  />
                </div>
              </div>
            </div>

            {/* Schedule */}
            <div className="border-b pb-4">
              <h4 className="text-md font-medium text-gray-900 mb-3">Schedule</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => handleChange('start_date', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => handleChange('end_date', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-3">Additional Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Project Color
                  </label>
                  <input
                    type="color"
                    value={formData.color}
                    onChange={(e) => handleChange('color', e.target.value)}
                    className="w-full h-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Comments
                  </label>
                  <textarea
                    rows="3"
                    value={formData.comment}
                    onChange={(e) => handleChange('comment', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter any additional comments or notes"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Create Project
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default WarehouseIssue;